import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import auditLogService from '../../../services/auditLogService';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const AuditLogs = () => {
  const { user } = useAuth();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statistics, setStatistics] = useState(null);
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const logsPerPage = 10;

  const actionTypeOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'profile_created', label: 'Profile Created' },
    { value: 'profile_updated', label: 'Profile Updated' },
    { value: 'profile_viewed', label: 'Profile Viewed' },
    { value: 'login', label: 'Login' },
    { value: 'logout', label: 'Logout' },
    { value: 'password_changed', label: 'Password Changed' },
    { value: 'email_changed', label: 'Email Changed' }
  ];

  useEffect(() => {
    if (user?.id) {
      loadAuditLogs();
      loadStatistics();
    }
  }, [user, filter, currentPage]);

  const loadAuditLogs = async () => {
    setLoading(true);
    try {
      const result = await auditLogService?.getUserAuditLogs(user?.id, {
        limit: logsPerPage,
        offset: currentPage * logsPerPage,
        actionType: filter === 'all' ? null : filter,
        orderBy: 'created_at',
        orderDirection: 'desc'
      });

      if (result?.success) {
        setAuditLogs(result?.data || []);
        setHasMore(result?.hasMore || false);
        setTotalCount(result?.count || 0);
      }
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const result = await auditLogService?.getAuditStatistics(user?.id);
      if (result?.success) {
        setStatistics(result?.data);
      }
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionIcon = (actionType) => {
    const icons = {
      profile_created: '✨',
      profile_updated: '✏️',
      profile_viewed: '👁️',
      login: '🔓',
      logout: '🔒',
      password_changed: '🔑',
      email_changed: '📧',
      data_export: '📥',
      data_deletion: '🗑️'
    };
    return icons?.[actionType] || '📝';
  };

  const getActionColor = (actionType, success) => {
    if (!success) return 'text-red-600';
    
    const colors = {
      profile_created: 'text-green-600',
      profile_updated: 'text-blue-600',
      profile_viewed: 'text-gray-600',
      login: 'text-green-600',
      logout: 'text-orange-600',
      password_changed: 'text-purple-600',
      email_changed: 'text-indigo-600'
    };
    return colors?.[actionType] || 'text-gray-600';
  };

  const handleFilterChange = (value) => {
    setFilter(value);
    setCurrentPage(0);
  };

  const handleNextPage = () => {
    if (hasMore) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  return (
    <div className="space-y-6">
      {/* Statistics Section */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-sm text-blue-600 font-medium">Total Actions</div>
            <div className="text-2xl font-bold text-blue-900 mt-1">{statistics?.total || 0}</div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-sm text-green-600 font-medium">Successful</div>
            <div className="text-2xl font-bold text-green-900 mt-1">{statistics?.successful || 0}</div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-sm text-red-600 font-medium">Failed</div>
            <div className="text-2xl font-bold text-red-900 mt-1">{statistics?.failed || 0}</div>
          </div>
        </div>
      )}

      {/* Filter Section */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-heading font-semibold text-foreground">
          Audit Trail
        </h3>
        <div className="w-64">
          <Select
            label="Filter by Action"
            value={filter}
            onChange={handleFilterChange}
            options={actionTypeOptions}
          />
        </div>
      </div>

      {/* Audit Logs List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : auditLogs?.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
          <p className="text-muted-foreground">No audit logs found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {auditLogs?.map((log) => (
            <div
              key={log?.id}
              className={`bg-white border rounded-lg p-4 hover:shadow-md transition-shadow ${
                log?.success ? 'border-gray-200' : 'border-red-200 bg-red-50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  <span className="text-2xl" role="img" aria-label={log?.action_type}>
                    {getActionIcon(log?.action_type)}
                  </span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${getActionColor(log?.action_type, log?.success)}`}>
                        {log?.action_description}
                      </span>
                      {!log?.success && (
                        <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                          Failed
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {formatDate(log?.created_at)}
                    </div>
                    
                    {/* Changed Fields */}
                    {log?.changed_fields && log?.changed_fields?.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-gray-700">Changed fields: </span>
                        <span className="text-xs text-gray-600">
                          {log?.changed_fields?.join(', ')}
                        </span>
                      </div>
                    )}
                    
                    {/* Error Message */}
                    {log?.error_message && (
                      <div className="mt-2 text-sm text-red-600">
                        Error: {log?.error_message}
                      </div>
                    )}
                    
                    {/* Technical Details */}
                    {(log?.ip_address || log?.user_agent) && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                          Technical Details
                        </summary>
                        <div className="mt-2 text-xs text-gray-600 space-y-1 pl-4">
                          {log?.ip_address && (
                            <div>IP Address: {log?.ip_address}</div>
                          )}
                          {log?.user_agent && (
                            <div>User Agent: {log?.user_agent?.substring(0, 80)}...</div>
                          )}
                          {log?.table_name && (
                            <div>Table: {log?.table_name}</div>
                          )}
                        </div>
                      </details>
                    )}
                    
                    {/* Value Changes */}
                    {log?.old_values && log?.new_values && (
                      <details className="mt-2">
                        <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">
                          View Changes
                        </summary>
                        <div className="mt-2 text-xs space-y-2 pl-4">
                          {log?.changed_fields?.map((field) => (
                            <div key={field} className="border-l-2 border-blue-300 pl-2">
                              <div className="font-medium text-gray-700">{field}</div>
                              <div className="text-red-600">- {JSON.stringify(log?.old_values?.[field])}</div>
                              <div className="text-green-600">+ {JSON.stringify(log?.new_values?.[field])}</div>
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalCount > logsPerPage && (
        <div className="flex items-center justify-between pt-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {currentPage * logsPerPage + 1} to {Math.min((currentPage + 1) * logsPerPage, totalCount)} of {totalCount} logs
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePreviousPage}
              disabled={currentPage === 0}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={!hasMore}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Compliance Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <div className="flex items-start gap-3">
          <span className="text-xl">ℹ️</span>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Audit Log Information</p>
            <p className="text-blue-700">
              All profile changes, access times, and data modifications are automatically logged for compliance and security purposes. 
              Logs are retained for verification of data persistence and cannot be modified or deleted by users.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogs;